import { useState } from "react";
import { ApiError } from "@/api/client";
import {
  embedSnippet,
  useCreateShare,
  useRevokeShare,
  useShares,
  useUpdateShareEmbed,
  type CreatedShare,
  type ShareLink,
  type ShareSubjectKind,
} from "@/api/queries";
import { Button } from "@/components/ui/Button";
import { Chip, type Tone } from "@/components/ui/Chip";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Field } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import { Textarea } from "@/components/ui/Textarea";
import { CheckIcon, CopyIcon, LinkIcon } from "@/components/icons";
import { relativeTime } from "@/lib/time";
import { useMeta } from "@/api/queries";

/**
 * View-only links to one report or dashboard (ACC-1).
 *
 * **The link is shown once.** Only a hash of its secret is stored, so after
 * this dialog closes nobody — including the person who made it — can read the
 * URL back. That is the same posture as the access key, and the dialog says so
 * rather than leaving someone to discover it by looking for a copy button that
 * is not there. Lost it? Revoke it and make another.
 *
 * Revoking asks first, as every destructive action here does, and the
 * consequence line is the part that matters: whoever holds the link loses
 * access on their next click, including a reader with the page open now.
 *
 * **A share can also be embedded** (HTML-2) on the sites named for it: the
 * embed URL is the link with `/embed/` in place of `/share/`, shown once with
 * it, and the `<iframe>` to paste is offered beside it. The sites can be
 * changed later from the list; the URL cannot be shown again.
 */

/** One origin per line (or comma), blanks dropped. The server checks each. */
function parseOrigins(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function CopyRow({ value, label, mono = true }: { value: string; label: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex gap-2">
      <input
        readOnly
        value={value}
        aria-label={label}
        onFocus={(event) => event.currentTarget.select()}
        className={`control-md rounded-element bg-card text-ink type-support border-border min-w-0 flex-1 border px-3 ${mono ? "font-mono" : ""}`}
      />
      <Button
        icon={copied ? <CheckIcon className="size-4" /> : <CopyIcon className="size-4" />}
        onClick={() => {
          void navigator.clipboard.writeText(value).then(() => setCopied(true));
        }}
      >
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}

/** A share's embedding sites, editable in place. */
function EmbedSites({
  share,
  kind,
  subjectId,
}: {
  share: ShareLink;
  kind: ShareSubjectKind;
  subjectId: string;
}) {
  const update = useUpdateShareEmbed(kind, subjectId);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(share.embed_origins.join("\n"));
  const error = update.error instanceof ApiError ? update.error.message : null;

  if (!editing) {
    return (
      <button
        type="button"
        className="type-caption text-accent normal-case hover:underline"
        onClick={() => {
          setText(share.embed_origins.join("\n"));
          setEditing(true);
        }}
      >
        {share.embed_origins.length
          ? `embeddable on ${share.embed_origins.length} site${share.embed_origins.length === 1 ? "" : "s"}`
          : "not embeddable"}
      </button>
    );
  }
  return (
    <div className="mt-2 flex flex-col gap-2">
      <Textarea
        label="Sites that may embed this link"
        rows={3}
        mono
        value={text}
        placeholder="https://www.example.com"
        hint="One per line. Empty stops embedding at once. The embed address is this share’s link with /share/ replaced by /embed/."
        onChange={(event) => setText(event.target.value)}
      />
      {error ? <p className="type-support text-error">{error}</p> : null}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="primary"
          loading={update.isPending}
          onClick={() =>
            update.mutate(
              { shareId: share.id, origins: parseOrigins(text) },
              { onSuccess: () => setEditing(false) },
            )
          }
        >
          Save sites
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

const EXPIRY_OPTIONS: Array<[string, number | null]> = [
  ["7 days", 7],
  ["30 days", 30],
  ["90 days", 90],
  ["Never", null],
];

const STATUS_TONE: Record<ShareLink["status"], Tone> = {
  active: "success",
  expired: "neutral",
  revoked: "error",
};

export function ShareDialog({
  open,
  onClose,
  kind,
  subjectId,
  subjectName,
}: {
  open: boolean;
  onClose: () => void;
  kind: ShareSubjectKind;
  subjectId: string;
  subjectName: string;
}) {
  const { data: meta } = useMeta();
  const locale = meta?.locale ?? "en-US";
  const { data: shares, isLoading } = useShares(kind, subjectId, open);
  const create = useCreateShare(kind, subjectId);
  const revoke = useRevokeShare(kind, subjectId);

  const [label, setLabel] = useState("");
  const [expiry, setExpiry] = useState("30");
  const [sites, setSites] = useState("");
  const [created, setCreated] = useState<CreatedShare | null>(null);
  const [revoking, setRevoking] = useState<ShareLink | null>(null);

  function close() {
    setCreated(null);
    setLabel("");
    setSites("");
    create.reset();
    onClose();
  }

  function submit() {
    create.mutate(
      {
        label,
        expires_in_days: expiry === "never" ? null : Number(expiry),
        embed_origins: parseOrigins(sites),
      },
      {
        onSuccess: (result) => {
          setCreated(result);
          setLabel("");
          setSites("");
        },
      },
    );
  }

  const error = create.error instanceof ApiError ? create.error.message : null;

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={`Share this ${kind}`}
        description={`Anyone with the link can read “${subjectName}” as of its last refresh. They cannot refresh it, ask questions, or open anything else.`}
        footer={<Button onClick={close}>Done</Button>}
      >
        <div className="flex flex-col gap-5">
          {created ? (
            <div className="bg-success-subtle rounded-element flex flex-col gap-2 p-3">
              <p className="type-label text-ink">Link created — copy it now</p>
              <CopyRow value={created.url} label="Share link" />
              {created.embed_url ? (
                <>
                  <p className="type-label text-ink mt-2">
                    Embed on {created.share.embed_origins.join(", ")}
                  </p>
                  <CopyRow
                    value={embedSnippet(created.embed_url, subjectName)}
                    label="Embed code"
                  />
                  <p className="type-support text-ink-secondary">
                    Paste this into a page on those sites. It shows the {kind} as of its last
                    refresh — a schedule keeps it current — with no SQL, and other sites cannot
                    frame it.
                  </p>
                </>
              ) : null}
              <p className="type-support text-ink-secondary">
                This is the only time the link is shown. If it is lost, revoke it and create
                another.
              </p>
            </div>
          ) : null}

          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
              <Field
                label="Who is it for?"
                placeholder="Board pack, Sam at Acme…"
                value={label}
                maxLength={200}
                onChange={(event) => setLabel(event.target.value)}
                hint="Only you see this, in the list below."
              />
              <Select
                label="Expires"
                value={expiry}
                onChange={(event) => setExpiry(event.target.value)}
              >
                {EXPIRY_OPTIONS.map(([text, days]) => (
                  <option key={text} value={days === null ? "never" : String(days)}>
                    {text}
                  </option>
                ))}
              </Select>
            </div>
            <Textarea
              label="Also embed it on these sites (optional)"
              rows={2}
              mono
              value={sites}
              placeholder="https://www.example.com"
              hint="One origin per line. Only these sites can show it in an iframe; leave empty for a link only."
              onChange={(event) => setSites(event.target.value)}
            />
            {error ? <p className="type-support text-error">{error}</p> : null}
            <div>
              <Button
                type="submit"
                variant="primary"
                icon={<LinkIcon className="size-4" />}
                loading={create.isPending}
              >
                Create link
              </Button>
            </div>
          </form>

          <section className="flex flex-col gap-2">
            <h3 className="type-label text-ink">Links</h3>
            {isLoading ? (
              <Spinner />
            ) : shares && shares.length ? (
              <ul className="divide-border border-border rounded-element divide-y border">
                {shares.map((share) => (
                  <li key={share.id} className="flex items-center gap-3 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="type-support text-ink truncate">
                        {share.label || "Unlabelled link"}
                      </p>
                      <p className="type-caption text-ink-disabled normal-case">
                        created {relativeTime(share.created_at, locale)}
                        {" · "}
                        {share.expires_at
                          ? `${share.status === "expired" ? "expired" : "expires"} ${relativeTime(share.expires_at, locale)}`
                          : "never expires"}
                        {" · "}
                        {share.use_count
                          ? `opened ${share.use_count} time${share.use_count === 1 ? "" : "s"}, last ${relativeTime(share.last_used_at, locale)}`
                          : "never opened"}
                      </p>
                      {share.status === "active" ? (
                        <EmbedSites share={share} kind={kind} subjectId={subjectId} />
                      ) : null}
                    </div>
                    <Chip tone={STATUS_TONE[share.status]}>{share.status}</Chip>
                    {share.status === "active" ? (
                      <Button size="sm" variant="danger" onClick={() => setRevoking(share)}>
                        Revoke
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="type-support text-ink-secondary">
                No links yet. Each one you create appears here, with when it was last opened.
              </p>
            )}
          </section>
        </div>
      </Modal>
      {/* A sibling, never a child: `showModal()` on a second dialog puts it on
          the top layer above this one, which is what makes this one inert
          while the question is answered. */}
      <ConfirmDialog
        open={Boolean(revoking)}
        onClose={() => setRevoking(null)}
        loading={revoke.isPending}
        title="Revoke this link?"
        description={<>“{revoking?.label || "Unlabelled link"}” will stop opening.</>}
        consequence={
          <>
            Anyone holding this link loses access — including a reader who has the page open now,
            on their next click.
          </>
        }
        confirmLabel="Revoke"
        onConfirm={() => {
          if (!revoking) return;
          revoke.mutate(revoking.id, { onSuccess: () => setRevoking(null) });
        }}
      />
    </>
  );
}
